import React, { useState, useMemo, useEffect, useRef } from "react";
import { Card, Table, Row, Col, Form, InputGroup, Button, Pagination, Dropdown, Badge, Modal, Alert, Spinner, Toast, ToastContainer, OverlayTrigger, Tooltip } from "react-bootstrap";
import Footer from "../layouts/Footer";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

const variablesParDefaut = [
  { variable: "{{name}}", description: "Nom du contact" },
  { variable: "{{code}}", description: "Code de vérification" },
  { variable: "{{prenom}}", description: "Prénom du contact" },
  { variable: "{{nom}}", description: "Nom du contact" },
  { variable: "{{entreprise}}", description: "Nom de l'entreprise" },
  { variable: "{{date}}", description: "Date du jour" },
  { variable: "{{heure}}", description: "Heure actuelle" }
];

export default function TemplatesManagement() {
  const [filterText, setFilterText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortField, setSortField] = useState('updated_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [notifications, setNotifications] = useState([]);
  const [alertMessage, setAlertMessage] = useState({ show: false, message: '', type: 'info' });

  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [previewContent, setPreviewContent] = useState("");
  const [templateToDelete, setTemplateToDelete] = useState(null);

  const [variablesDisponibles, setVariablesDisponibles] = useState(variablesParDefaut);
  const [loadingVariables, setLoadingVariables] = useState(false);

  const [templateForm, setTemplateForm] = useState({
    name: '',
    content: '',
    category: 'Général'
  });

  const initDoneRef = useRef(false);

  // ============================================
  // NOTIFICATIONS
  // ============================================
  const showNotification = (message, type = 'info', duration = 5000) => {
    const id = Date.now();
    setNotifications(prev => [{ id, message, type, show: true }, ...prev]);
    setTimeout(() => hideNotification(id), duration);
  };

  const hideNotification = (id) => {
    setNotifications(prev => prev.filter(notif => notif.id !== id));
  };

  const showAlert = (message, type = 'warning', duration = 5000) => {
    setAlertMessage({ show: true, message, type });
    if (duration > 0) setTimeout(() => hideAlert(), duration);
  };

  const hideAlert = () => {
    setAlertMessage({ show: false, message: '', type: 'info' });
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'success': return 'ri-checkbox-circle-fill';
      case 'error': return 'ri-error-warning-fill';
      case 'warning': return 'ri-alert-fill';
      case 'info': return 'ri-information-fill';
      default: return 'ri-notification-3-fill';
    }
  };

  const getNotificationBg = (type) => {
    switch (type) {
      case 'success': return 'success';
      case 'error': return 'danger';
      case 'warning': return 'warning';
      case 'info': return 'info';
      default: return 'primary';
    }
  };

  // ============================================
  // CHARGEMENT DES VARIABLES
  // ============================================
  const loadVariables = async () => {
    try {
      setLoadingVariables(true);
      const response = await apiClient.get('/variables');
      const variablesData = response.data;

      if (variablesData && Array.isArray(variablesData)) {
        const variablesFormatees = variablesData.map(variable => ({
          variable: `{{${variable.name || variable.variable}}}`,
          description: variable.description || `Variable ${variable.name || variable.variable}`
        }));
        setVariablesDisponibles(variablesFormatees);
      } else {
        setVariablesDisponibles(variablesParDefaut);
      }
    } catch (error) {
      console.error('❌ Erreur variables:', error);
      setVariablesDisponibles(variablesParDefaut);
    } finally {
      setLoadingVariables(false);
    }
  };

  // ============================================
  // INSERTION DE VARIABLE
  // ============================================
  const insererVariable = (variable) => {
    const textarea = document.querySelector('textarea[name="content"]');
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = templateForm.content;
      const newText = text.substring(0, start) + variable + text.substring(end);

      setTemplateForm({ ...templateForm, content: newText });

      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + variable.length, start + variable.length);
      }, 0);
    }
  };

  // ============================================
  // CHARGEMENT DES TEMPLATES
  // ============================================
  const loadTemplates = async () => {
    try {
      setLoadingTemplates(true);
      const response = await apiClient.get('/sms-templates');
      const templatesData = response.data;

      if (!templatesData || !Array.isArray(templatesData)) {
        setTemplates([]);
        return;
      }
      setTemplates(templatesData);
    } catch (error) {
      console.error('❌ Erreur templates:', error);
      showAlert(`Erreur lors du chargement: ${error.message}`, 'danger');
    } finally {
      setLoadingTemplates(false);
    }
  };

  // ============================================
  // CRÉATION
  // ============================================
  const handleCreateTemplate = async () => {
    if (!templateForm.name?.trim() || !templateForm.content?.trim()) {
      showAlert("Veuillez remplir tous les champs obligatoires", 'warning');
      return;
    }

    setCreateLoading(true);
    try {
      const templateData = {
        name: templateForm.name.trim(),
        content: templateForm.content.trim(),
        category: templateForm.category || 'Général'
      };

      const response = await apiClient.post('/sms-templates', templateData);
      const newTemplate = response.data;

      setTemplates(prev => [newTemplate, ...prev]);
      setTemplateForm({ name: '', content: '', category: 'Général' });
      setShowCreateModal(false);
      showNotification(`Template "${templateForm.name}" créé avec succès`, 'success');
      loadTemplates();
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      showAlert(`Erreur lors de la création: ${errorMessage}`, 'error');
    } finally {
      setCreateLoading(false);
    }
  };

  // ============================================
  // MISE À JOUR
  // ============================================
  const handleUpdateTemplate = async () => {
    if (!selectedTemplate?.code_template || !templateForm.name?.trim() || !templateForm.content?.trim()) {
      showAlert("Veuillez remplir tous les champs obligatoires", 'warning');
      return;
    }

    setUpdateLoading(true);
    try {
      const templateData = {
        name: templateForm.name.trim(),
        content: templateForm.content.trim(),
        category: templateForm.category || 'Général'
      };

      const response = await apiClient.put(`/sms-templates/${selectedTemplate.code_template}`, templateData);
      const updatedTemplate = response.data;

      setTemplates(prev =>
        prev.map(t => t.code_template === selectedTemplate.code_template ? updatedTemplate : t)
      );

      setShowEditModal(false);
      setSelectedTemplate(null);
      setTemplateForm({ name: '', content: '', category: 'Général' });
      showNotification(`Template "${templateForm.name}" mis à jour`, 'success');
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      showAlert(`Erreur lors de la mise à jour: ${errorMessage}`, 'error');
    } finally {
      setUpdateLoading(false);
    }
  };

  // ============================================
  // SUPPRESSION
  // ============================================
  const handleOpenDeleteModal = (template) => {
    setTemplateToDelete(template);
    setShowDeleteModal(true);
  };

  const handleDeleteTemplate = async () => {
    if (!templateToDelete?.code_template) return;

    setDeleteLoading(true);
    try {
      await apiClient.delete(`/sms-templates/${templateToDelete.code_template}`);
      setTemplates(prev => prev.filter(t => t.code_template !== templateToDelete.code_template));
      setShowDeleteModal(false);
      setTemplateToDelete(null);
      showNotification("Template supprimé avec succès", 'success');
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      showAlert(`Erreur lors de la suppression: ${errorMessage}`, 'error');
    } finally {
      setDeleteLoading(false);
    }
  };

  // ============================================
  // CHARGEMENT INITIAL
  // ============================================
  useEffect(() => {
    if (initDoneRef.current) return;
    initDoneRef.current = true;
    loadTemplates();
    loadVariables();
  }, []);

  // ============================================
  // HANDLERS MODALES
  // ============================================
  const handleEditTemplate = (template) => {
    setSelectedTemplate(template);
    setTemplateForm({
      name: template.name || '',
      content: template.content || '',
      category: template.category || 'Général'
    });
    setShowEditModal(true);
  };

  const handlePreviewTemplate = (template) => {
    setSelectedTemplate(template);
    setPreviewContent(template.content || '');
    setShowPreviewModal(true);
  };

  // ============================================
  // FILTRAGE ET TRI
  // ============================================
  const filteredAndSortedItems = useMemo(() => {
    if (!Array.isArray(templates)) return [];

    let filtered = templates.filter(template => {
      const searchTerm = filterText.toLowerCase();
      const templateName = template.name || '';
      const templateContent = template.content || '';
      const templateCategory = template.category || '';

      return templateName.toLowerCase().includes(searchTerm) ||
             templateContent.toLowerCase().includes(searchTerm) ||
             templateCategory.toLowerCase().includes(searchTerm);
    });

    filtered.sort((a, b) => {
      let aValue = a[sortField] || '';
      let bValue = b[sortField] || '';

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (sortDirection === 'asc') return aValue > bValue ? 1 : -1;
      return aValue < bValue ? 1 : -1;
    });

    return filtered;
  }, [templates, filterText, sortField, sortDirection]);

  // ============================================
  // PAGINATION
  // ============================================
  const totalPages = Math.ceil(filteredAndSortedItems.length / itemsPerPage) || 1;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = filteredAndSortedItems.slice(startIndex, startIndex + itemsPerPage);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // ============================================
  // SÉLECTION
  // ============================================
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedTemplates(currentItems.map(template => template.code_template).filter(Boolean));
    } else {
      setSelectedTemplates([]);
    }
  };

  const handleSelectTemplate = (templateCode) => {
    if (!templateCode) return;
    setSelectedTemplates(prev =>
      prev.includes(templateCode)
        ? prev.filter(code => code !== templateCode)
        : [...prev, templateCode]
    );
  };

  const isAllSelected = currentItems.length > 0 &&
    selectedTemplates.length === currentItems.length &&
    currentItems.every(item => item.code_template);

  const isSomeSelected = selectedTemplates.length > 0 &&
    selectedTemplates.length < currentItems.length;

  // ============================================
  // STATS
  // ============================================
  const stats = useMemo(() => {
    const total = templates.length;
    const withContent = templates.filter(t => t.content && t.content.trim()).length;
    const recentlyUpdated = templates.filter(t => {
      if (!t.updated_at) return false;
      const diff = (new Date() - new Date(t.updated_at)) / (1000 * 60 * 60 * 24);
      return diff <= 7;
    }).length;
    return { total, withContent, recentlyUpdated };
  }, [templates]);

  // ============================================
  // SUB-COMPONENTS
  // ============================================
  const SortableHeader = ({ field, children }) => (
    <th className="tmpl-sortable-header" onClick={() => handleSort(field)}>
      <span className="tmpl-sortable-content">
        {children}
        {sortField === field && (
          <i className={`ri-arrow-${sortDirection === 'asc' ? 'up' : 'down'}-line tmpl-sort-icon`}></i>
        )}
      </span>
    </th>
  );

  return (
    <div className="tmpl-page main main-app p-3 p-lg-4">
      {/* ========== TOASTS ========== */}
      <ToastContainer position="top-end" className="p-3" style={{ zIndex: 9999 }}>
        {notifications.map((notification) => {
          const bgColor = getNotificationBg(notification.type);
          const icon = getNotificationIcon(notification.type);
          return (
            <Toast
              key={notification.id}
              show={notification.show}
              onClose={() => hideNotification(notification.id)}
              delay={8000}
              autohide
              bg={bgColor}
            >
              <Toast.Header className={`bg-${bgColor} text-white`}>
                <i className={`${icon} me-2`}></i>
                <strong className="me-auto">
                  {notification.type === 'success' ? 'Succès' :
                   notification.type === 'error' ? 'Erreur' :
                   notification.type === 'warning' ? 'Attention' : 'Information'}
                </strong>
                <small>{new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</small>
              </Toast.Header>
              <Toast.Body className="text-white">{notification.message}</Toast.Body>
            </Toast>
          );
        })}
      </ToastContainer>

      {/* ========== ALERT ========== */}
      {alertMessage.show && (
        <div className={`tmpl-alert tmpl-alert-${alertMessage.type} mb-4`}>
          <i className={getNotificationIcon(alertMessage.type)}></i>
          <div className="flex-grow-1">{alertMessage.message}</div>
          <button className="tmpl-alert-close" onClick={hideAlert}>
            <i className="ri-close-line"></i>
          </button>
        </div>
      )}

      {/* ========== HEADER ========== */}
      <div className="tmpl-header mb-4">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
          <div className="d-flex align-items-center gap-3">
            <div className="tmpl-header-icon">
              <i className="ri-file-text-line"></i>
            </div>
            <div>
              <h3 className="tmpl-header-title mb-1">Templates SMS</h3>
              <p className="tmpl-header-subtitle mb-0">
                Créez et gérez vos modèles de messages prédéfinis
              </p>
            </div>
          </div>
          <div className="d-flex gap-2">
            <Button className="tmpl-btn-secondary" onClick={loadTemplates} disabled={loadingTemplates}>
              <i className={`ri-refresh-line ${loadingTemplates ? 'spinning' : ''}`}></i>
              <span>Actualiser</span>
            </Button>
            <Button className="tmpl-btn-primary" onClick={() => setShowCreateModal(true)}>
              <i className="ri-add-line"></i>
              <span>Nouveau template</span>
            </Button>
          </div>
        </div>
      </div>

      {/* ========== STATS ========== */}
      <Row className="g-3 mb-4">
        <Col sm={6} lg={4}>
          <div className="tmpl-stat-card tmpl-stat-blue">
            <div className="tmpl-stat-icon-wrapper tmpl-stat-icon-blue">
              <i className="ri-file-text-line"></i>
            </div>
            <div className="tmpl-stat-content">
              <div className="tmpl-stat-label">Total templates</div>
              <div className="tmpl-stat-value">{stats.total.toLocaleString('fr-FR')}</div>
            </div>
          </div>
        </Col>
        <Col sm={6} lg={4}>
          <div className="tmpl-stat-card tmpl-stat-green">
            <div className="tmpl-stat-icon-wrapper tmpl-stat-icon-green">
              <i className="ri-checkbox-circle-line"></i>
            </div>
            <div className="tmpl-stat-content">
              <div className="tmpl-stat-label">Avec contenu</div>
              <div className="tmpl-stat-value">{stats.withContent.toLocaleString('fr-FR')}</div>
            </div>
          </div>
        </Col>
        <Col sm={12} lg={4}>
          <div className="tmpl-stat-card tmpl-stat-violet">
            <div className="tmpl-stat-icon-wrapper tmpl-stat-icon-violet">
              <i className="ri-time-line"></i>
            </div>
            <div className="tmpl-stat-content">
              <div className="tmpl-stat-label">Modifiés (7j)</div>
              <div className="tmpl-stat-value">{stats.recentlyUpdated.toLocaleString('fr-FR')}</div>
            </div>
          </div>
        </Col>
      </Row>

      {/* ========== FILTRES ========== */}
      <div className="tmpl-filters mb-4">
        <Row className="g-3 align-items-center">
          <Col lg={6} md={12}>
            <div className="tmpl-search-wrapper">
              <i className="ri-search-line tmpl-search-icon"></i>
              <input
                type="text"
                className="tmpl-search-input"
                placeholder="Rechercher par nom, contenu ou catégorie..."
                value={filterText}
                onChange={e => { setFilterText(e.target.value); setCurrentPage(1); }}
              />
              {filterText && (
                <button className="tmpl-search-clear" onClick={() => setFilterText('')}>
                  <i className="ri-close-line"></i>
                </button>
              )}
            </div>
          </Col>
          <Col lg={3} md={6}>
            <select
              className="tmpl-select"
              value={itemsPerPage}
              onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
            >
              <option value={5}>5 par page</option>
              <option value={10}>10 par page</option>
              <option value={25}>25 par page</option>
              <option value={50}>50 par page</option>
            </select>
          </Col>
          <Col lg={3} md={6} className="text-md-end">
            <span className="tmpl-filter-info">
              <i className="ri-information-line"></i>
              <strong>{filteredAndSortedItems.length}</strong> template{filteredAndSortedItems.length > 1 ? 's' : ''}
            </span>
          </Col>
        </Row>
      </div>

      {/* ========== TABLE ========== */}
      <div className="tmpl-table-card">
        {loadingTemplates ? (
          <div className="tmpl-loading">
            <div className="tmpl-loading-spinner"></div>
            <div className="tmpl-loading-text">Chargement des templates...</div>
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <Table className="tmpl-table mb-0">
                <thead>
                  <tr>
                    <th style={{ width: '50px' }}>
                      <div className="tmpl-checkbox-wrapper">
                        <input
                          type="checkbox"
                          className="tmpl-checkbox"
                          checked={isAllSelected}
                          ref={input => { if (input) input.indeterminate = isSomeSelected; }}
                          onChange={handleSelectAll}
                        />
                      </div>
                    </th>
                    <SortableHeader field="name">
                      <i className="ri-text me-1"></i>Nom
                    </SortableHeader>
                    <SortableHeader field="content">
                      <i className="ri-file-text-line me-1"></i>Contenu
                    </SortableHeader>
                    <SortableHeader field="created_at">
                      <i className="ri-calendar-line me-1"></i>Créé le
                    </SortableHeader>
                    <SortableHeader field="updated_at">
                      <i className="ri-time-line me-1"></i>Modifié le
                    </SortableHeader>
                    <th className="text-end" style={{ width: '140px' }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.length > 0 ? (
                    currentItems.map((template) => (
                      <tr key={template.code_template || template.id} className="tmpl-row">
                        <td>
                          <div className="tmpl-checkbox-wrapper">
                            <input
                              type="checkbox"
                              className="tmpl-checkbox"
                              checked={selectedTemplates.includes(template.code_template)}
                              onChange={() => handleSelectTemplate(template.code_template)}
                            />
                          </div>
                        </td>
                        <td>
                          <div className="tmpl-name-cell">
                            <div className="tmpl-name-main">{template.name || 'Sans nom'}</div>
                            {template.code_template && (
                              <code className="tmpl-code">{template.code_template}</code>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="tmpl-content-cell" title={template.content}>
                            {template.content || <span className="text-muted">Aucun contenu</span>}
                          </div>
                        </td>
                        <td>
                          <div className="tmpl-date-cell">
                            {template.created_at ? new Date(template.created_at).toLocaleDateString('fr-FR') : '—'}
                          </div>
                        </td>
                        <td>
                          <div className="tmpl-date-cell">
                            {template.updated_at ? new Date(template.updated_at).toLocaleDateString('fr-FR') : '—'}
                          </div>
                        </td>
                        <td className="text-end">
                          <div className="tmpl-actions">
                            <OverlayTrigger placement="top" overlay={<Tooltip>Aperçu</Tooltip>}>
                              <button
                                className="tmpl-action-btn tmpl-action-view"
                                onClick={() => handlePreviewTemplate(template)}
                              >
                                <i className="ri-eye-line"></i>
                              </button>
                            </OverlayTrigger>
                            <OverlayTrigger placement="top" overlay={<Tooltip>Modifier</Tooltip>}>
                              <button
                                className="tmpl-action-btn tmpl-action-edit"
                                onClick={() => handleEditTemplate(template)}
                              >
                                <i className="ri-pencil-line"></i>
                              </button>
                            </OverlayTrigger>
                            <OverlayTrigger placement="top" overlay={<Tooltip>Supprimer</Tooltip>}>
                              <button
                                className="tmpl-action-btn tmpl-action-delete"
                                onClick={() => handleOpenDeleteModal(template)}
                                disabled={deleteLoading || !template.code_template}
                              >
                                <i className="ri-delete-bin-line"></i>
                              </button>
                            </OverlayTrigger>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6">
                        <div className="tmpl-empty-state">
                          <div className="tmpl-empty-icon">
                            <i className="ri-file-text-line"></i>
                          </div>
                          <h5 className="tmpl-empty-title">Aucun template trouvé</h5>
                          <p className="tmpl-empty-text">
                            {filterText
                              ? "Aucun template ne correspond à votre recherche"
                              : "Commencez par créer votre premier template"}
                          </p>
                          {filterText ? (
                            <button
                              className="tmpl-btn-empty-reset"
                              onClick={() => setFilterText('')}
                            >
                              <i className="ri-refresh-line me-2"></i>
                              Réinitialiser
                            </button>
                          ) : (
                            <button
                              className="tmpl-btn-empty-reset"
                              onClick={() => setShowCreateModal(true)}
                            >
                              <i className="ri-add-line me-2"></i>
                              Créer un template
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            {/* ========== PAGINATION ========== */}
            {filteredAndSortedItems.length > 0 && totalPages > 1 && (
              <div className="tmpl-pagination">
                <div className="tmpl-pagination-info">
                  <i className="ri-information-line"></i>
                  <strong>{startIndex + 1}</strong> à <strong>{Math.min(startIndex + itemsPerPage, filteredAndSortedItems.length)}</strong> sur <strong>{filteredAndSortedItems.length}</strong>
                  {selectedTemplates.length > 0 && (
                    <span className="tmpl-pagination-selected">
                      • <strong>{selectedTemplates.length}</strong> sélectionné{selectedTemplates.length > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                <div className="tmpl-pagination-controls">
                  <button
                    className="tmpl-page-btn"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(currentPage - 1)}
                  >
                    <i className="ri-arrow-left-s-line"></i>
                  </button>
                  {[...Array(totalPages)].slice(0, 5).map((_, index) => {
                    const pageNum = index + 1;
                    return (
                      <button
                        key={pageNum}
                        className={`tmpl-page-btn ${currentPage === pageNum ? 'active' : ''}`}
                        onClick={() => setCurrentPage(pageNum)}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  {totalPages > 5 && <span className="tmpl-page-ellipsis">...</span>}
                  <button
                    className="tmpl-page-btn"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(currentPage + 1)}
                  >
                    <i className="ri-arrow-right-s-line"></i>
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ========== MODAL CRÉATION ========== */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg" centered className="tmpl-modal">
        <div className="tmpl-modal-header tmpl-modal-header-primary">
          <div className="d-flex align-items-center gap-3">
            <div className="tmpl-modal-icon tmpl-modal-icon-primary">
              <i className="ri-add-line"></i>
            </div>
            <div>
              <h5 className="tmpl-modal-title mb-0">Créer un template</h5>
              <small className="tmpl-modal-subtitle">Nouveau modèle de message</small>
            </div>
          </div>
          <button className="tmpl-modal-close" onClick={() => setShowCreateModal(false)}>
            <i className="ri-close-line"></i>
          </button>
        </div>

        <Modal.Body className="tmpl-modal-body">
          <div className="tmpl-info-alert">
            <i className="ri-information-line"></i>
            <div>Utilisez les variables entre doubles accolades <code>{'{{variable}}'}</code> pour personnaliser vos messages.</div>
          </div>

          <div className="tmpl-form-group">
            <label className="tmpl-form-label">
              Nom du template <span className="tmpl-required">*</span>
            </label>
            <input
              type="text"
              className="tmpl-input"
              placeholder="Ex: Code de vérification"
              value={templateForm.name}
              onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
            />
          </div>

          <div className="tmpl-form-group">
            <label className="tmpl-form-label">
              Contenu du message <span className="tmpl-required">*</span>
            </label>
            <textarea
              className="tmpl-textarea"
              rows={4}
              placeholder="Rédigez votre template ici..."
              value={templateForm.content}
              onChange={(e) => setTemplateForm({ ...templateForm, content: e.target.value })}
              name="content"
            />
            <div className="tmpl-form-hint">
              <i className="ri-information-line"></i>
              Utilisez les variables disponibles ci-dessous
            </div>
          </div>

          <div className="tmpl-variables-section">
            <div className="tmpl-variables-header">
              <div className="d-flex align-items-center gap-2">
                <i className="ri-code-line"></i>
                <span className="fw-bold">Variables disponibles</span>
              </div>
              <button
                className="tmpl-btn-tiny"
                onClick={loadVariables}
                disabled={loadingVariables}
              >
                <i className={`ri-refresh-line ${loadingVariables ? 'spinning' : ''}`}></i>
              </button>
            </div>

            {loadingVariables ? (
              <div className="tmpl-loading-small">
                <div className="tmpl-loading-spinner-small"></div>
                <span>Chargement...</span>
              </div>
            ) : (
              <div className="tmpl-variables-grid">
                {variablesDisponibles.map((variable, index) => (
                  <button
                    key={index}
                    className="tmpl-variable-chip"
                    onClick={() => insererVariable(variable.variable)}
                    title={variable.description}
                  >
                    <i className="ri-add-line"></i>
                    <code>{variable.variable}</code>
                  </button>
                ))}
              </div>
            )}
          </div>
        </Modal.Body>

        <div className="tmpl-modal-footer">
          <button className="tmpl-btn-cancel" onClick={() => setShowCreateModal(false)}>
            Annuler
          </button>
          <button
            className="tmpl-btn-primary"
            onClick={handleCreateTemplate}
            disabled={createLoading || !templateForm.name?.trim() || !templateForm.content?.trim()}
          >
            {createLoading ? (
              <>
                <Spinner animation="border" size="sm" />
                Création...
              </>
            ) : (
              <>
                <i className="ri-check-line"></i>
                Créer le template
              </>
            )}
          </button>
        </div>
      </Modal>

      {/* ========== MODAL ÉDITION ========== */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg" centered className="tmpl-modal">
        <div className="tmpl-modal-header tmpl-modal-header-blue">
          <div className="d-flex align-items-center gap-3">
            <div className="tmpl-modal-icon tmpl-modal-icon-blue">
              <i className="ri-pencil-line"></i>
            </div>
            <div>
              <h5 className="tmpl-modal-title mb-0">Modifier le template</h5>
              <small className="tmpl-modal-subtitle">{selectedTemplate?.name || ''}</small>
            </div>
          </div>
          <button className="tmpl-modal-close" onClick={() => setShowEditModal(false)}>
            <i className="ri-close-line"></i>
          </button>
        </div>

        <Modal.Body className="tmpl-modal-body">
          {selectedTemplate && (
            <>
              <div className="tmpl-form-group">
                <label className="tmpl-form-label">
                  Nom du template <span className="tmpl-required">*</span>
                </label>
                <input
                  type="text"
                  className="tmpl-input"
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                />
              </div>

              <div className="tmpl-form-group">
                <label className="tmpl-form-label">
                  Contenu du message <span className="tmpl-required">*</span>
                </label>
                <textarea
                  className="tmpl-textarea"
                  rows={4}
                  value={templateForm.content}
                  onChange={(e) => setTemplateForm({ ...templateForm, content: e.target.value })}
                  name="content"
                />
              </div>

              <div className="tmpl-variables-section">
                <div className="tmpl-variables-header">
                  <div className="d-flex align-items-center gap-2">
                    <i className="ri-code-line"></i>
                    <span className="fw-bold">Variables disponibles</span>
                  </div>
                  <button
                    className="tmpl-btn-tiny"
                    onClick={loadVariables}
                    disabled={loadingVariables}
                  >
                    <i className={`ri-refresh-line ${loadingVariables ? 'spinning' : ''}`}></i>
                  </button>
                </div>

                {loadingVariables ? (
                  <div className="tmpl-loading-small">
                    <div className="tmpl-loading-spinner-small"></div>
                    <span>Chargement...</span>
                  </div>
                ) : (
                  <div className="tmpl-variables-grid">
                    {variablesDisponibles.map((variable, index) => (
                      <button
                        key={index}
                        className="tmpl-variable-chip"
                        onClick={() => insererVariable(variable.variable)}
                        title={variable.description}
                      >
                        <i className="ri-add-line"></i>
                        <code>{variable.variable}</code>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </Modal.Body>

        <div className="tmpl-modal-footer">
          <button className="tmpl-btn-cancel" onClick={() => setShowEditModal(false)}>
            Annuler
          </button>
          <button
            className="tmpl-btn-primary"
            onClick={handleUpdateTemplate}
            disabled={updateLoading || !templateForm.name?.trim() || !templateForm.content?.trim()}
          >
            {updateLoading ? (
              <>
                <Spinner animation="border" size="sm" />
                Mise à jour...
              </>
            ) : (
              <>
                <i className="ri-check-line"></i>
                Mettre à jour
              </>
            )}
          </button>
        </div>
      </Modal>

      {/* ========== MODAL APERÇU ========== */}
      <Modal show={showPreviewModal} onHide={() => setShowPreviewModal(false)} size="lg" centered className="tmpl-modal">
        <div className="tmpl-modal-header tmpl-modal-header-dark">
          <div className="d-flex align-items-center gap-3">
            <div className="tmpl-modal-icon tmpl-modal-icon-dark">
              <i className="ri-eye-line"></i>
            </div>
            <div>
              <h5 className="tmpl-modal-title mb-0">Aperçu du template</h5>
              <small className="tmpl-modal-subtitle">{selectedTemplate?.name || ''}</small>
            </div>
          </div>
          <button className="tmpl-modal-close" onClick={() => setShowPreviewModal(false)}>
            <i className="ri-close-line"></i>
          </button>
        </div>

        <Modal.Body className="tmpl-modal-body">
          {selectedTemplate && (
            <>
              <div className="tmpl-preview-header">
                <h5 className="tmpl-preview-title">{selectedTemplate.name || 'Sans nom'}</h5>
                <div className="tmpl-preview-badges">
                  <span className="tmpl-preview-badge">{selectedTemplate.category || 'Non catégorisé'}</span>
                  {selectedTemplate.code_template && (
                    <span className="tmpl-preview-badge tmpl-preview-badge-blue">
                      {selectedTemplate.code_template}
                    </span>
                  )}
                </div>
              </div>

              <div className="tmpl-preview-content">
                <div className="tmpl-preview-content-header">
                  <i className="ri-chat-quote-line"></i>
                  Contenu du message
                </div>
                <div className="tmpl-preview-content-body">
                  {previewContent || 'Aucun contenu'}
                </div>
              </div>
            </>
          )}
        </Modal.Body>

        <div className="tmpl-modal-footer">
          <button className="tmpl-btn-cancel" onClick={() => setShowPreviewModal(false)}>
            Fermer
          </button>
        </div>
      </Modal>

      {/* ========== MODAL SUPPRESSION ========== */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered className="tmpl-modal">
        <Modal.Body className="tmpl-delete-body">
          <div className="tmpl-delete-icon-wrapper">
            <div className="tmpl-delete-icon">
              <i className="ri-delete-bin-6-line"></i>
            </div>
          </div>

          <h4 className="tmpl-delete-title">Confirmer la suppression</h4>

          {templateToDelete && (
            <>
              <p className="tmpl-delete-text">
                Êtes-vous sûr de vouloir supprimer définitivement ce template ?
              </p>

              <div className="tmpl-delete-template">
                <div className="tmpl-delete-template-name">{templateToDelete.name || 'Sans nom'}</div>
                {templateToDelete.content && (
                  <div className="tmpl-delete-template-content">
                    {templateToDelete.content.length > 120
                      ? `${templateToDelete.content.substring(0, 120)}...`
                      : templateToDelete.content}
                  </div>
                )}
              </div>

              <div className="tmpl-delete-warning">
                <i className="ri-alert-line"></i>
                Cette action est irréversible
              </div>
            </>
          )}

          <div className="tmpl-delete-actions">
            <button
              className="tmpl-btn-cancel tmpl-btn-flex"
              onClick={() => setShowDeleteModal(false)}
              disabled={deleteLoading}
            >
              <i className="ri-close-line"></i>
              Annuler
            </button>
            <button
              className="tmpl-btn-danger tmpl-btn-flex"
              onClick={handleDeleteTemplate}
              disabled={deleteLoading}
            >
              {deleteLoading ? (
                <>
                  <Spinner animation="border" size="sm" />
                  Suppression...
                </>
              ) : (
                <>
                  <i className="ri-delete-bin-line"></i>
                  Supprimer
                </>
              )}
            </button>
          </div>
        </Modal.Body>
      </Modal>

      <Footer />

      {/* ========== STYLES PREMIUM ========== */}
      <style>{`
        /* ========== GLOBAL ========== */
        .tmpl-page {
          background: #f8fafc;
          min-height: 100vh;
        }
        .tmpl-page * {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        /* ========== HEADER ========== */
        .tmpl-header {
          background: linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%);
          border-radius: 16px;
          padding: 24px 28px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 1px 3px rgba(0,0,0,0.03);
        }
        .tmpl-header-icon {
          width: 56px;
          height: 56px;
          border-radius: 14px;
          background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 26px;
          box-shadow: 0 8px 20px -6px rgba(59, 130, 246, 0.5);
        }
        .tmpl-header-title {
          font-weight: 700;
          color: #0f172a;
          font-size: 1.5rem;
          letter-spacing: -0.02em;
        }
        .tmpl-header-subtitle {
          color: #64748b;
          font-size: 0.875rem;
        }

        /* ========== BUTTONS ========== */
        .tmpl-btn-primary, .tmpl-btn-secondary, .tmpl-btn-cancel, .tmpl-btn-danger, .tmpl-btn-tiny {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          border-radius: 10px;
          font-weight: 600;
          border: none;
          transition: all 0.2s;
          cursor: pointer;
          text-decoration: none;
        }
        .tmpl-btn-primary {
          padding: 11px 20px;
          font-size: 0.875rem;
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          color: white;
        }
        .tmpl-btn-primary:hover:not(:disabled) {
          background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
          transform: translateY(-1px);
          box-shadow: 0 8px 16px -4px rgba(59, 130, 246, 0.4);
        }
        .tmpl-btn-primary:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .tmpl-btn-secondary {
          padding: 11px 20px;
          font-size: 0.875rem;
          background: white;
          border: 1px solid #e2e8f0;
          color: #334155;
        }
        .tmpl-btn-secondary:hover:not(:disabled) {
          background: #f8fafc;
          border-color: #cbd5e1;
        }
        .tmpl-btn-secondary:disabled {
          opacity: 0.6;
        }
        .tmpl-btn-cancel {
          padding: 11px 20px;
          font-size: 0.875rem;
          background: white;
          border: 1px solid #e2e8f0;
          color: #334155;
        }
        .tmpl-btn-cancel:hover {
          background: #f1f5f9;
        }
        .tmpl-btn-danger {
          padding: 11px 20px;
          font-size: 0.875rem;
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
          color: white;
        }
        .tmpl-btn-danger:hover:not(:disabled) {
          background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
          transform: translateY(-1px);
          box-shadow: 0 8px 16px -4px rgba(239, 68, 68, 0.4);
        }
        .tmpl-btn-danger:disabled {
          opacity: 0.6;
        }
        .tmpl-btn-tiny {
          padding: 6px 10px;
          font-size: 0.8rem;
          background: white;
          border: 1px solid #e2e8f0;
          color: #64748b;
        }
        .tmpl-btn-tiny:hover {
          background: #3b82f6;
          color: white;
          border-color: #3b82f6;
        }
        .tmpl-btn-flex {
          flex: 1;
        }

        /* ========== STATS ========== */
        .tmpl-stat-card {
          display: flex;
          align-items: center;
          gap: 14px;
          background: white;
          border-radius: 14px;
          padding: 18px 20px;
          border: 1px solid #e2e8f0;
          transition: all 0.25s;
        }
        .tmpl-stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px -6px rgba(0,0,0,0.08);
        }
        .tmpl-stat-icon-wrapper {
          width: 46px;
          height: 46px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          flex-shrink: 0;
        }
        .tmpl-stat-icon-blue   { background: #eff6ff; color: #3b82f6; }
        .tmpl-stat-icon-green  { background: #d1fae5; color: #10b981; }
        .tmpl-stat-icon-violet { background: #ede9fe; color: #8b5cf6; }
        .tmpl-stat-content {
          min-width: 0;
          flex-grow: 1;
        }
        .tmpl-stat-label {
          font-size: 0.72rem;
          color: #64748b;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 3px;
        }
        .tmpl-stat-value {
          font-size: 1.5rem;
          font-weight: 700;
          color: #0f172a;
          letter-spacing: -0.02em;
          line-height: 1;
        }

        /* ========== ALERT ========== */
        .tmpl-alert {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px 18px;
          border-radius: 12px;
          font-size: 0.85rem;
          font-weight: 500;
        }
        .tmpl-alert > i {
          font-size: 1.3rem;
          flex-shrink: 0;
        }
        .tmpl-alert-success {
          background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
          color: #065f46;
          border-left: 4px solid #10b981;
        }
        .tmpl-alert-danger {
          background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
          color: #991b1b;
          border-left: 4px solid #ef4444;
        }
        .tmpl-alert-warning {
          background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
          color: #92400e;
          border-left: 4px solid #f59e0b;
        }
        .tmpl-alert-info {
          background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
          color: #1e40af;
          border-left: 4px solid #3b82f6;
        }
        .tmpl-alert-close {
          background: none;
          border: none;
          color: inherit;
          opacity: 0.6;
          cursor: pointer;
          padding: 4px 8px;
          border-radius: 6px;
          transition: all 0.15s;
          font-size: 1rem;
        }
        .tmpl-alert-close:hover {
          opacity: 1;
          background: rgba(0,0,0,0.05);
        }

        /* ========== FILTRES ========== */
        .tmpl-filters {
          background: white;
          border-radius: 14px;
          padding: 16px 20px;
          border: 1px solid #e2e8f0;
        }
        .tmpl-search-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }
        .tmpl-search-icon {
          position: absolute;
          left: 14px;
          color: #94a3b8;
          font-size: 1.05rem;
          pointer-events: none;
        }
        .tmpl-search-input {
          width: 100%;
          padding: 11px 40px 11px 42px;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          font-size: 0.875rem;
          background: #f8fafc;
          transition: all 0.2s;
          outline: none;
          font-family: inherit;
        }
        .tmpl-search-input:focus {
          background: white;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }
        .tmpl-search-clear {
          position: absolute;
          right: 8px;
          background: none;
          border: none;
          color: #94a3b8;
          font-size: 1.1rem;
          padding: 6px 10px;
          cursor: pointer;
          border-radius: 6px;
          transition: all 0.15s;
        }
        .tmpl-search-clear:hover {
          background: #fee2e2;
          color: #ef4444;
        }
        .tmpl-select {
          width: 100%;
          padding: 11px 14px;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          font-size: 0.875rem;
          font-weight: 500;
          background: #f8fafc;
          color: #334155;
          outline: none;
          transition: all 0.2s;
          cursor: pointer;
          font-family: inherit;
        }
        .tmpl-select:focus {
          background: white;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }
        .tmpl-filter-info {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 11px 16px;
          background: #f1f5f9;
          border-radius: 10px;
          color: #64748b;
          font-size: 0.8rem;
          font-weight: 500;
        }
        .tmpl-filter-info strong {
          color: #3b82f6;
        }

        /* ========== TABLE ========== */
        .tmpl-table-card {
          background: white;
          border-radius: 16px;
          border: 1px solid #e2e8f0;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0,0,0,0.03);
        }
        .tmpl-table {
          font-size: 0.875rem;
        }
        .tmpl-table thead th {
          background: #f8fafc;
          color: #475569;
          font-weight: 700;
          font-size: 0.7rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          padding: 16px;
          border-bottom: 1px solid #e2e8f0;
          border-top: none;
          white-space: nowrap;
        }
        .tmpl-table tbody td {
          padding: 16px;
          vertical-align: middle;
          border-bottom: 1px solid #f1f5f9;
        }
        .tmpl-row {
          transition: background-color 0.15s;
        }
        .tmpl-row:hover {
          background-color: #f8fafc;
        }
        .tmpl-sortable-header {
          cursor: pointer;
          user-select: none;
        }
        .tmpl-sortable-header:hover {
          color: #3b82f6;
        }
        .tmpl-sortable-content {
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .tmpl-sort-icon {
          color: #3b82f6;
          font-size: 0.9rem;
        }

        /* Checkbox */
        .tmpl-checkbox-wrapper {
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .tmpl-checkbox {
          width: 18px;
          height: 18px;
          border-radius: 5px;
          border: 1.5px solid #cbd5e1;
          cursor: pointer;
          accent-color: #3b82f6;
        }

        /* Name cell */
        .tmpl-name-cell {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .tmpl-name-main {
          font-weight: 700;
          color: #0f172a;
          font-size: 0.875rem;
        }
        .tmpl-code {
          font-size: 0.7rem;
          color: #7c3aed;
          background: #ede9fe;
          padding: 3px 8px;
          border-radius: 5px;
          font-family: 'SF Mono', Menlo, Monaco, monospace;
          display: inline-block;
          width: fit-content;
        }

        /* Content cell */
        .tmpl-content-cell {
          max-width: 320px;
          overflow: hidden;
          text-overflow: ellipsis;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          color: #64748b;
          font-size: 0.82rem;
          line-height: 1.4;
        }

        /* Date cell */
        .tmpl-date-cell {
          color: #64748b;
          font-size: 0.8rem;
          font-variant-numeric: tabular-nums;
        }

        /* Actions */
        .tmpl-actions {
          display: inline-flex;
          gap: 6px;
          justify-content: flex-end;
        }
        .tmpl-action-btn {
          width: 34px;
          height: 34px;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
          background: white;
          color: #64748b;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.15s;
          font-size: 0.95rem;
        }
        .tmpl-action-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .tmpl-action-view:hover:not(:disabled) {
          background: #06b6d4;
          border-color: #06b6d4;
          color: white;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px -2px rgba(6, 182, 212, 0.4);
        }
        .tmpl-action-edit:hover:not(:disabled) {
          background: #3b82f6;
          border-color: #3b82f6;
          color: white;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px -2px rgba(59, 130, 246, 0.4);
        }
        .tmpl-action-delete:hover:not(:disabled) {
          background: #ef4444;
          border-color: #ef4444;
          color: white;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px -2px rgba(239, 68, 68, 0.4);
        }

        /* ========== LOADING / EMPTY ========== */
        .tmpl-loading {
          padding: 80px 20px;
          text-align: center;
        }
        .tmpl-loading-spinner {
          width: 48px;
          height: 48px;
          border: 4px solid #e2e8f0;
          border-top-color: #3b82f6;
          border-radius: 50%;
          animation: tmpl-spin 0.8s linear infinite;
          margin: 0 auto 16px;
        }
        .tmpl-loading-text {
          color: #64748b;
          font-size: 0.875rem;
          font-weight: 500;
        }
        .tmpl-loading-small {
          padding: 20px;
          display: flex;
          align-items: center;
          gap: 10px;
          color: #94a3b8;
          font-size: 0.8rem;
        }
        .tmpl-loading-spinner-small {
          width: 20px;
          height: 20px;
          border: 2px solid #e2e8f0;
          border-top-color: #3b82f6;
          border-radius: 50%;
          animation: tmpl-spin 0.8s linear infinite;
        }
        .tmpl-empty-state {
          padding: 60px 20px;
          text-align: center;
        }
        .tmpl-empty-icon {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          background: #f1f5f9;
          color: #94a3b8;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 36px;
          margin-bottom: 20px;
        }
        .tmpl-empty-title {
          color: #334155;
          font-weight: 700;
          margin-bottom: 8px;
        }
        .tmpl-empty-text {
          color: #94a3b8;
          margin-bottom: 20px;
        }
        .tmpl-btn-empty-reset {
          display: inline-flex;
          align-items: center;
          background: white;
          color: #3b82f6;
          border: 1px solid #bfdbfe;
          border-radius: 8px;
          font-weight: 600;
          padding: 8px 16px;
          text-decoration: none;
          transition: all 0.2s;
          cursor: pointer;
        }
        .tmpl-btn-empty-reset:hover {
          background: #3b82f6;
          color: white;
          border-color: #3b82f6;
        }

        /* ========== PAGINATION ========== */
        .tmpl-pagination {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 24px;
          background: #f8fafc;
          border-top: 1px solid #e2e8f0;
          flex-wrap: wrap;
          gap: 12px;
        }
        .tmpl-pagination-info {
          font-size: 0.8rem;
          color: #64748b;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .tmpl-pagination-info strong {
          color: #0f172a;
        }
        .tmpl-pagination-selected {
          color: #3b82f6;
          margin-left: 8px;
        }
        .tmpl-pagination-controls {
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .tmpl-page-btn {
          min-width: 34px;
          height: 34px;
          padding: 0 10px;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
          background: white;
          color: #475569;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.15s;
          font-size: 0.8rem;
          font-weight: 600;
        }
        .tmpl-page-btn:hover:not(:disabled) {
          background: #3b82f6;
          color: white;
          border-color: #3b82f6;
        }
        .tmpl-page-btn:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }
        .tmpl-page-btn.active {
          background: #3b82f6;
          color: white;
          border-color: #3b82f6;
        }
        .tmpl-page-ellipsis {
          color: #94a3b8;
          font-size: 0.8rem;
        }

        /* ========== MODAL ========== */
        .tmpl-modal .modal-content {
          border: none;
          border-radius: 16px;
          overflow: hidden;
        }
        .tmpl-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          color: white;
        }
        .tmpl-modal-header-primary {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
        }
        .tmpl-modal-header-blue {
          background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
        }
        .tmpl-modal-header-dark {
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
        }
        .tmpl-modal-icon {
          width: 44px;
          height: 44px;
          border-radius: 10px;
          background: rgba(255,255,255,0.2);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
        }
        .tmpl-modal-title {
          font-weight: 700;
          letter-spacing: -0.01em;
        }
        .tmpl-modal-subtitle {
          color: rgba(255,255,255,0.8);
          font-size: 0.8rem;
        }
        .tmpl-modal-close {
          background: rgba(255,255,255,0.1);
          border: none;
          color: white;
          width: 36px;
          height: 36px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
          font-size: 1.1rem;
        }
        .tmpl-modal-close:hover {
          background: rgba(255,255,255,0.2);
          transform: rotate(90deg);
        }
        .tmpl-modal-body {
          padding: 24px;
          background: #f8fafc;
          max-height: 65vh;
          overflow-y: auto;
        }
        .tmpl-modal-footer {
          padding: 16px 24px;
          background: white;
          border-top: 1px solid #e2e8f0;
          display: flex;
          justify-content: flex-end;
          gap: 10px;
        }

        /* Info alert in modal */
        .tmpl-info-alert {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 14px 18px;
          background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
          color: #1e40af;
          border-radius: 12px;
          font-size: 0.82rem;
          margin-bottom: 20px;
          border-left: 4px solid #3b82f6;
          line-height: 1.5;
        }
        .tmpl-info-alert > i {
          font-size: 1.2rem;
          flex-shrink: 0;
          margin-top: 1px;
        }
        .tmpl-info-alert code {
          background: white;
          color: #7c3aed;
          padding: 2px 8px;
          border-radius: 5px;
          font-weight: 700;
        }

        /* Form */
        .tmpl-form-group {
          margin-bottom: 18px;
        }
        .tmpl-form-label {
          display: block;
          font-weight: 700;
          color: #0f172a;
          font-size: 0.85rem;
          margin-bottom: 8px;
        }
        .tmpl-required {
          color: #ef4444;
        }
        .tmpl-input, .tmpl-textarea {
          width: 100%;
          padding: 12px 16px;
          border: 2px solid #e2e8f0;
          border-radius: 10px;
          font-size: 0.875rem;
          color: #0f172a;
          background: white;
          transition: all 0.2s;
          outline: none;
          font-family: inherit;
        }
        .tmpl-input:focus, .tmpl-textarea:focus {
          border-color: #3b82f6;
          box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
        }
        .tmpl-textarea {
          resize: vertical;
          line-height: 1.5;
        }
        .tmpl-form-hint {
          margin-top: 8px;
          font-size: 0.75rem;
          color: #94a3b8;
          display: flex;
          align-items: center;
          gap: 5px;
        }
        .tmpl-form-hint i {
          color: #3b82f6;
        }

        /* Variables */
        .tmpl-variables-section {
          background: white;
          border-radius: 12px;
          border: 1px solid #e2e8f0;
          padding: 16px;
        }
        .tmpl-variables-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 14px;
          color: #334155;
          font-size: 0.85rem;
        }
        .tmpl-variables-header > div > i {
          color: #3b82f6;
          font-size: 1.1rem;
        }
        .tmpl-variables-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 8px;
        }
        .tmpl-variable-chip {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.15s;
          font-family: inherit;
          text-align: left;
        }
        .tmpl-variable-chip:hover {
          background: #eff6ff;
          border-color: #3b82f6;
          transform: translateY(-1px);
        }
        .tmpl-variable-chip > i {
          color: #3b82f6;
          font-size: 0.9rem;
          flex-shrink: 0;
        }
        .tmpl-variable-chip code {
          font-family: 'SF Mono', Menlo, Monaco, monospace;
          font-size: 0.75rem;
          color: #1e40af;
          background: transparent;
          font-weight: 600;
        }

        /* Preview */
        .tmpl-preview-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          flex-wrap: wrap;
          gap: 12px;
          margin-bottom: 20px;
        }
        .tmpl-preview-title {
          font-weight: 700;
          color: #0f172a;
          margin: 0;
          font-size: 1.15rem;
        }
        .tmpl-preview-badges {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }
        .tmpl-preview-badge {
          display: inline-flex;
          align-items: center;
          padding: 5px 12px;
          border-radius: 8px;
          font-size: 0.72rem;
          font-weight: 700;
          background: #f1f5f9;
          color: #475569;
        }
        .tmpl-preview-badge-blue {
          background: #eff6ff;
          color: #3b82f6;
        }
        .tmpl-preview-content {
          background: white;
          border-radius: 12px;
          border: 1px solid #e2e8f0;
          overflow: hidden;
        }
        .tmpl-preview-content-header {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 16px;
          background: #f8fafc;
          border-bottom: 1px solid #e2e8f0;
          font-weight: 700;
          color: #334155;
          font-size: 0.82rem;
        }
        .tmpl-preview-content-header i {
          color: #3b82f6;
        }
        .tmpl-preview-content-body {
          padding: 20px;
          white-space: pre-line;
          line-height: 1.6;
          color: #334155;
          font-size: 0.9rem;
          min-height: 100px;
        }

        /* Delete Modal */
        .tmpl-delete-body {
          padding: 32px 24px 24px;
          text-align: center;
        }
        .tmpl-delete-icon-wrapper {
          margin-bottom: 20px;
        }
        .tmpl-delete-icon {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
          color: #dc2626;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 36px;
          box-shadow: 0 12px 24px -8px rgba(220, 38, 38, 0.3);
        }
        .tmpl-delete-title {
          font-weight: 700;
          color: #0f172a;
          margin-bottom: 12px;
          font-size: 1.25rem;
        }
        .tmpl-delete-text {
          color: #64748b;
          margin-bottom: 20px;
          font-size: 0.875rem;
        }
        .tmpl-delete-template {
          background: #f8fafc;
          border-radius: 12px;
          padding: 16px 20px;
          margin-bottom: 16px;
          border: 1px solid #e2e8f0;
          text-align: left;
        }
        .tmpl-delete-template-name {
          font-weight: 700;
          color: #0f172a;
          font-size: 0.95rem;
          margin-bottom: 6px;
        }
        .tmpl-delete-template-content {
          color: #64748b;
          font-size: 0.78rem;
          line-height: 1.4;
        }
        .tmpl-delete-warning {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 8px 14px;
          background: #fef2f2;
          color: #dc2626;
          border-radius: 8px;
          font-size: 0.78rem;
          font-weight: 700;
          margin-bottom: 20px;
        }
        .tmpl-delete-actions {
          display: flex;
          gap: 10px;
        }

        /* ========== ANIMATIONS ========== */
        .spinning {
          animation: tmpl-spin 1s linear infinite;
          display: inline-block;
        }
        @keyframes tmpl-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        /* ========== RESPONSIVE ========== */
        @media (max-width: 768px) {
          .tmpl-header {
            padding: 20px;
          }
          .tmpl-header-title {
            font-size: 1.2rem;
          }
          .tmpl-pagination {
            flex-direction: column;
          }
          .tmpl-stat-value {
            font-size: 1.25rem;
          }
          .tmpl-variables-grid {
            grid-template-columns: 1fr;
          }
          .tmpl-delete-actions {
            flex-direction: column;
          }
        }
      `}</style>
    </div>
  );
}